/* eslint-disable react-refresh/only-export-components -- option builders live with HFChart */
import { useMemo, useRef } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import type { EChartsOption } from 'echarts';
import { useHfThemeMode, type HfThemeMode } from '../../theme';
import { composeSx, formatAxisValue, formatMetric } from '../../utils';
import { contrastTextColor } from '../../utils/contrast';
import { chartPalette, useHFChartPaletteOverride, type HFChartPalette } from './palette';
import { useEChart } from './useEChart';

/** Hide stacked in-bar labels smaller than this share of the tallest stacked column. */
const STACKED_LABEL_MIN_RATIO = 0.1;

/** `containLabel` already reserves axis-label space — keep plot insets tight. */
const CARTESIAN_GRID = { top: 36, right: 8, bottom: 24, left: 4, containLabel: true as const };

function cartesianGrid(overrides?: { top?: number; right?: number; bottom?: number; left?: number }) {
  return { ...CARTESIAN_GRID, ...overrides };
}

export type HFChartSeries = {
  name: string;
  data: number[];
  color?: string;
  /** Stack id; defaults to `'total'` when `barMode` is `'stacked'` or `'floating'`. */
  stack?: string;
};

export type HFPiePoint = {
  name: string;
  value: number;
};

export type HFScatterPoint = {
  x: number;
  y: number;
  name?: string;
};

export type HFBubblePoint = {
  x: number;
  y: number;
  size: number;
  name?: string;
};

/** OHLC candle values (named fields — engine adapters choose positional order). */
export type HFOhlcPoint = {
  open: number;
  high: number;
  low: number;
  close: number;
};

export type HFScatterSeries = {
  name: string;
  data: HFScatterPoint[];
  color?: string;
};

export type HFBubbleSeries = {
  name: string;
  data: HFBubblePoint[];
  color?: string;
};

/**
 * Bar layout modes (engine-agnostic):
 * - `stacked` — stack series, value axis includes 0
 * - `floating` — stack series, value axis follows data (not percent/full-stacked)
 * - `diverging` — series[0] long / series[1] short styling
 */
export type HFBarMode = 'grouped' | 'stacked' | 'floating' | 'diverging';

type HFChartBaseProps = {
  /** Pixel height, or a CSS length (e.g. `'100%'`) when the chart should fill its parent. */
  height?: number | string;
  ariaLabel?: string;
  sx?: SxProps<Theme>;
};

type CartesianSeriesProps = {
  categories: string[];
  series: HFChartSeries[];
};

export type HFChartProps =
  | (HFChartBaseProps &
      CartesianSeriesProps & {
        variant: 'line' | 'area' | 'splineArea';
      })
  | (HFChartBaseProps &
      CartesianSeriesProps & {
        variant: 'bar';
        barMode?: HFBarMode;
        showLabels?: boolean;
        valueAxisSuffix?: string;
        categoryAxisPosition?: 'bottom' | 'top';
        /** Color each bar green/red from its signed value (ignored for diverging mode). */
        colorBySign?: boolean;
      })
  | (HFChartBaseProps & {
      variant: 'combo';
      categories: string[];
      lineSeries: HFChartSeries[];
      barSeries: HFChartSeries[];
      barValueAxisSuffix?: string;
      lineValueAxisSuffix?: string;
      /** Color combo bars green/red from signed values. */
      colorBySign?: boolean;
    })
  | (HFChartBaseProps & {
      variant: 'pie';
      data: HFPiePoint[];
      donut?: boolean;
    })
  | (HFChartBaseProps & {
      variant: 'funnel' | 'pyramid';
      data: HFPiePoint[];
    })
  | (HFChartBaseProps & {
      variant: 'scatter';
      series: HFScatterSeries[];
    })
  | (HFChartBaseProps & {
      variant: 'bubble';
      series: HFBubbleSeries[];
    })
  | (HFChartBaseProps & {
      variant: 'candlestick';
      categories: string[];
      data: HFOhlcPoint[];
    });

type HFChartBuildInput =
  | {
      variant: 'line' | 'area' | 'splineArea';
      mode: HfThemeMode;
      palette?: HFChartPalette;
      categories: string[];
      series: HFChartSeries[];
    }
  | {
      variant: 'bar';
      mode: HfThemeMode;
      palette?: HFChartPalette;
      categories: string[];
      series: HFChartSeries[];
      barMode?: HFBarMode;
      showLabels?: boolean;
      valueAxisSuffix?: string;
      categoryAxisPosition?: 'bottom' | 'top';
      colorBySign?: boolean;
    }
  | {
      variant: 'combo';
      mode: HfThemeMode;
      palette?: HFChartPalette;
      categories: string[];
      lineSeries: HFChartSeries[];
      barSeries: HFChartSeries[];
      barValueAxisSuffix?: string;
      lineValueAxisSuffix?: string;
      colorBySign?: boolean;
    }
  | {
      variant: 'pie';
      mode: HfThemeMode;
      palette?: HFChartPalette;
      data: HFPiePoint[];
      donut?: boolean;
    }
  | {
      variant: 'funnel' | 'pyramid';
      mode: HfThemeMode;
      palette?: HFChartPalette;
      data: HFPiePoint[];
    }
  | {
      variant: 'scatter';
      mode: HfThemeMode;
      palette?: HFChartPalette;
      series: HFScatterSeries[];
    }
  | {
      variant: 'bubble';
      mode: HfThemeMode;
      palette?: HFChartPalette;
      series: HFBubbleSeries[];
    }
  | {
      variant: 'candlestick';
      mode: HfThemeMode;
      palette?: HFChartPalette;
      categories: string[];
      data: HFOhlcPoint[];
    };

function resolvePalette(input: { mode: HfThemeMode; palette?: HFChartPalette }): HFChartPalette {
  return input.palette ?? chartPalette(input.mode);
}

function cartesianChrome(palette: HFChartPalette) {
  return {
    backgroundColor: 'transparent' as const,
    tooltip: { trigger: 'axis' as const },
    textStyle: { color: palette.legendText },
  };
}

export function buildLineOption(input: Extract<HFChartBuildInput, { variant: 'line' | 'area' | 'splineArea' }>): EChartsOption {
  const { categories, series, variant } = input;
  const palette = resolvePalette(input);
  const filled = variant === 'area' || variant === 'splineArea';
  const smooth = variant === 'splineArea';

  return {
    ...cartesianChrome(palette),
    color: palette.series,
    grid: cartesianGrid(),
    legend: { top: 10, right: 8, textStyle: { color: palette.legendText } },
    xAxis: { type: 'category', data: categories, axisLabel: { color: palette.axisLabelColor } },
    yAxis: {
      type: 'value',
      scale: true,
      min: 'dataMin',
      max: 'dataMax',
      axisLabel: {
        color: palette.axisLabelColor,
        hideOverlap: true,
        formatter: (value: string | number) => formatMetric(Number(value)),
      },
      splitLine: { show: true, lineStyle: { color: palette.gridColor } },
    },
    series: series.map((entry) => ({
      type: 'line' as const,
      name: entry.name,
      data: entry.data,
      smooth: smooth ? 0.35 : 0,
      showSymbol: false,
      lineStyle: { width: 2, color: entry.color },
      itemStyle: entry.color ? { color: entry.color } : undefined,
      areaStyle: filled ? { opacity: 0.35 } : undefined,
    })),
  };
}

export function buildBarOption(input: Extract<HFChartBuildInput, { variant: 'bar' }>): EChartsOption {
  const {
    categories,
    series,
    barMode = 'grouped',
    showLabels = false,
    valueAxisSuffix,
    categoryAxisPosition = 'bottom',
    colorBySign = false,
  } = input;
  const palette = resolvePalette(input);
  const baselineStacked = barMode === 'stacked';
  const floatingStacked = barMode === 'floating';
  const stacked = baselineStacked || floatingStacked;
  const diverging = barMode === 'diverging';
  const topCategories = categoryAxisPosition === 'top';
  const signed = colorBySign && !diverging;

  const colors = diverging ? series.map((_, index) => (index === 0 ? palette.long : palette.short)) : stacked ? palette.categorical : palette.series;

  const maxStackAbs = Math.max(
    0.0001,
    ...categories.map((_, catIndex) => series.reduce((sum, entry) => sum + Math.abs(entry.data[catIndex] ?? 0), 0)),
  );
  const stackedLabelMinAbs = maxStackAbs * STACKED_LABEL_MIN_RATIO;

  const signColor = (value: number) => (value < 0 ? palette.negative : palette.positive);

  return {
    ...cartesianChrome(palette),
    color: colors,
    grid: cartesianGrid({
      // Diverging: add room under the plot for the horizontal Long/Short legend
      // so it never sits on top of the left % axis labels.
      top: topCategories ? 72 : CARTESIAN_GRID.top,
      bottom: (topCategories ? 28 : CARTESIAN_GRID.bottom) + (diverging ? 16 : 0),
    }),
    legend: diverging
      ? { bottom: 4, orient: 'horizontal', textStyle: { color: palette.legendText } }
      : { top: 10, right: 8, textStyle: { color: palette.legendText } },
    xAxis: {
      type: 'category',
      data: categories,
      position: categoryAxisPosition,
      axisLabel: {
        color: palette.axisLabelColor,
        rotate: topCategories || categories.some((label) => label.length > 8) ? 35 : 0,
      },
      axisTick: { alignWithLabel: true },
    },
    yAxis: {
      type: 'value',
      // stacked: always include 0 (bars sit on the X axis). floating: follow data range.
      ...(baselineStacked ? { min: 0 } : {}),
      ...(floatingStacked ? { scale: true, min: 'dataMin' as const, max: 'dataMax' as const } : {}),
      axisLabel: {
        color: palette.axisLabelColor,
        formatter: (value: string | number) => formatAxisValue(value, valueAxisSuffix),
      },
      splitLine: { show: true, lineStyle: { color: palette.gridColor } },
    },
    series: series.map((entry, index) => {
      const fill: string = entry.color ?? colors[index % colors.length] ?? palette.legendText;
      const labelColor: string = stacked ? contrastTextColor(fill) : palette.legendText;
      return {
        type: 'bar' as const,
        name: entry.name,
        data: signed
          ? entry.data.map((value) => ({
              value,
              itemStyle: { color: entry.color ?? signColor(value) },
            }))
          : entry.data,
        stack: stacked ? (entry.stack ?? 'total') : entry.stack,
        itemStyle: entry.color || diverging ? { color: fill } : undefined,
        label: showLabels
          ? {
              show: true,
              position: diverging ? (index === 0 ? 'top' : 'bottom') : stacked ? 'inside' : 'top',
              color: labelColor,
              fontWeight: 700,
              fontSize: 10,
              formatter: (params: { value?: unknown }) => {
                const raw = params.value;
                const n = typeof raw === 'number' ? raw : raw && typeof raw === 'object' && 'value' in raw ? Number(raw.value) : Number(raw);
                if (!Number.isFinite(n) || n === 0) return '';
                if (stacked && Math.abs(n) < stackedLabelMinAbs) return '';
                return formatAxisValue(n, valueAxisSuffix);
              },
            }
          : undefined,
      };
    }),
  };
}

export function buildComboOption(input: Extract<HFChartBuildInput, { variant: 'combo' }>): EChartsOption {
  const { categories, lineSeries, barSeries, barValueAxisSuffix, lineValueAxisSuffix, colorBySign = false } = input;
  const palette = resolvePalette(input);
  const signColor = (value: number) => (value < 0 ? palette.negative : palette.positive);

  return {
    ...cartesianChrome(palette),
    color: palette.series,
    grid: cartesianGrid(),
    legend: { top: 10, right: 8, textStyle: { color: palette.legendText } },
    xAxis: { type: 'category', data: categories, axisLabel: { color: palette.axisLabelColor } },
    yAxis: [
      {
        type: 'value',
        scale: true,
        axisLabel: {
          color: palette.axisLabelColor,
          formatter: (value: string | number) => formatAxisValue(value, lineValueAxisSuffix),
        },
        splitLine: { show: true, lineStyle: { color: palette.gridColor } },
      },
      {
        type: 'value',
        axisLabel: {
          color: palette.axisLabelColor,
          formatter: (value: string | number) => formatAxisValue(value, barValueAxisSuffix),
        },
        splitLine: { show: false },
      },
    ],
    series: [
      ...barSeries.map((entry, index) => ({
        type: 'bar' as const,
        name: entry.name,
        data: colorBySign
          ? entry.data.map((value) => ({
              value,
              itemStyle: { color: entry.color ?? signColor(value) },
            }))
          : entry.data,
        yAxisIndex: 1,
        itemStyle: entry.color ? { color: entry.color } : colorBySign ? undefined : { color: palette.series[index % palette.series.length] },
      })),
      ...lineSeries.map((entry, index) => ({
        type: 'line' as const,
        name: entry.name,
        data: entry.data,
        yAxisIndex: 0,
        showSymbol: false,
        lineStyle: { width: 2, color: entry.color ?? palette.series[(barSeries.length + index) % palette.series.length] },
        itemStyle: entry.color ? { color: entry.color } : undefined,
      })),
    ],
  };
}

export function buildPieOption(input: Extract<HFChartBuildInput, { variant: 'pie' }>): EChartsOption {
  const { data, donut = false } = input;
  const palette = resolvePalette(input);
  const total = data.reduce((sum, entry) => sum + entry.value, 0);

  return {
    backgroundColor: 'transparent',
    color: palette.categorical,
    legend: {
      right: 8,
      top: 'middle',
      orient: 'vertical',
      textStyle: { color: palette.legendText },
      formatter: (name: string) => {
        const point = data.find((entry) => entry.name === name);
        if (!point || total <= 0) return name;
        return `${name} ${((point.value / total) * 100).toFixed(1)}%`;
      },
    },
    tooltip: { trigger: 'item' },
    series: [
      {
        type: 'pie',
        radius: donut ? ['45%', '72%'] : '70%',
        center: ['38%', '50%'],
        label: { color: palette.legendText, formatter: '{d}%' },
        data,
      },
    ],
  };
}

export function buildFunnelOption(input: Extract<HFChartBuildInput, { variant: 'funnel' | 'pyramid' }>): EChartsOption {
  const { data, variant } = input;
  const palette = resolvePalette(input);
  const pyramid = variant === 'pyramid';

  return {
    backgroundColor: 'transparent',
    color: palette.categorical,
    tooltip: { trigger: 'item', formatter: '{b}: {c}' },
    legend: { top: 10, right: 8, textStyle: { color: palette.legendText } },
    series: [
      {
        type: 'funnel',
        left: '8%',
        top: 48,
        bottom: 24,
        width: '72%',
        minSize: pyramid ? '10%' : '0%',
        maxSize: '100%',
        sort: pyramid ? 'ascending' : 'descending',
        gap: 2,
        label: { color: palette.legendText, formatter: '{b}' },
        itemStyle: { borderColor: 'transparent', borderWidth: 0 },
        data,
      },
    ],
  };
}

export function buildScatterOption(input: Extract<HFChartBuildInput, { variant: 'scatter' }>): EChartsOption {
  const { series } = input;
  const palette = resolvePalette(input);

  return {
    ...cartesianChrome(palette),
    color: palette.series,
    grid: cartesianGrid(),
    legend: { top: 10, right: 8, textStyle: { color: palette.legendText } },
    tooltip: { trigger: 'item' },
    xAxis: {
      type: 'value',
      axisLabel: { color: palette.axisLabelColor },
      splitLine: { show: true, lineStyle: { color: palette.gridColor } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: palette.axisLabelColor },
      splitLine: { show: true, lineStyle: { color: palette.gridColor } },
    },
    series: series.map((entry) => ({
      type: 'scatter' as const,
      name: entry.name,
      data: entry.data.map((point) => ({
        value: [point.x, point.y],
        name: point.name,
      })),
      symbolSize: 10,
      itemStyle: entry.color ? { color: entry.color } : undefined,
    })),
  };
}

export function buildBubbleOption(input: Extract<HFChartBuildInput, { variant: 'bubble' }>): EChartsOption {
  const { series } = input;
  const palette = resolvePalette(input);
  const sizes = series.flatMap((entry) => entry.data.map((point) => point.size));
  const maxSize = Math.max(1, ...sizes);

  return {
    ...cartesianChrome(palette),
    color: palette.series,
    grid: cartesianGrid(),
    legend: { top: 10, right: 8, textStyle: { color: palette.legendText } },
    tooltip: { trigger: 'item' },
    xAxis: {
      type: 'value',
      axisLabel: { color: palette.axisLabelColor },
      splitLine: { show: true, lineStyle: { color: palette.gridColor } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: palette.axisLabelColor },
      splitLine: { show: true, lineStyle: { color: palette.gridColor } },
    },
    series: series.map((entry) => ({
      type: 'scatter' as const,
      name: entry.name,
      data: entry.data.map((point) => ({
        value: [point.x, point.y, point.size],
        name: point.name,
      })),
      symbolSize: (value: unknown) => {
        const size = Array.isArray(value) ? Number(value[2]) : 0;
        return 8 + (Math.sqrt(Math.max(0, size)) / Math.sqrt(maxSize)) * 40;
      },
      itemStyle: { opacity: 0.7, ...(entry.color ? { color: entry.color } : {}) },
    })),
  };
}

export function buildCandlestickOption(input: Extract<HFChartBuildInput, { variant: 'candlestick' }>): EChartsOption {
  const { categories, data } = input;
  const palette = resolvePalette(input);

  return {
    ...cartesianChrome(palette),
    grid: cartesianGrid(),
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: { color: palette.axisLabelColor },
      boundaryGap: true,
    },
    yAxis: {
      type: 'value',
      scale: true,
      axisLabel: {
        color: palette.axisLabelColor,
        formatter: (value: string | number) => formatMetric(Number(value)),
      },
      splitLine: { show: true, lineStyle: { color: palette.gridColor } },
    },
    series: [
      {
        type: 'candlestick',
        data: data.map((point) => [point.open, point.close, point.low, point.high]),
        itemStyle: {
          color: palette.long,
          color0: palette.short,
          borderColor: palette.long,
          borderColor0: palette.short,
        },
      },
    ],
  };
}

export function buildOption(input: HFChartBuildInput): EChartsOption {
  switch (input.variant) {
    case 'line':
    case 'area':
    case 'splineArea':
      return buildLineOption(input);
    case 'bar':
      return buildBarOption(input);
    case 'combo':
      return buildComboOption(input);
    case 'pie':
      return buildPieOption(input);
    case 'funnel':
    case 'pyramid':
      return buildFunnelOption(input);
    case 'scatter':
      return buildScatterOption(input);
    case 'bubble':
      return buildBubbleOption(input);
    case 'candlestick':
      return buildCandlestickOption(input);
  }
}

/** ECharts chart — cartesian, pie/funnel, scatter/bubble, candlestick, and combo variants. */
export function HFChart({ height = 320, ariaLabel, sx, ...props }: HFChartProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useHfThemeMode();
  const palette = useHFChartPaletteOverride();

  const option = useMemo(() => buildOption({ ...props, mode, palette: palette ?? undefined }), [mode, props, palette]);
  useEChart(rootRef, option);

  return <Box ref={rootRef} role="img" aria-label={ariaLabel ?? `${props.variant} chart`} sx={composeSx({ width: '100%', height }, sx)} />;
}

export type { HFChartPalette } from './palette';
export type { HFDateSeriesRow } from '../../utils';
